import { useEffect, useState } from "react";
import { Textblock } from "@/services/backend/textblocks/types/textblock";
import {
  ListRequest,
  useTextblocksTextblockListQuery,
} from "@/services/backend/textblocks/api/textblock-api";
import { CreateTextblockDialog } from "@/services/backend/textblocks/components/create-textblock-dialog";
import { TextblocksTable } from "@/services/backend/textblocks/components/textblocks-table";
import { DocumentId, DocumentKind, SubdomainName } from "@bitory-gmbh/bsm";
import { DeleteTextblocksDialog } from "@/services/backend/textblocks/components/delete-textblocks-dialog";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { ChevronUp, Copy } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { UUID } from "@bitory-gmbh/nidavellir";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { formatDate } from "@/shared/lib/utilities/date";
import {
  useTextblocksCopyFromCatalogMutation,
  CpyName,
} from "@/services/backend/textblocks/api/copy-from-catalog-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";
import { useTextblocksCatalogListQuery } from "../api/catalog-api";

export function Textblocks({
  subdomain,
  documentKind,
  documentId,
  title = t("Bemerkungen"),
  defaultOpen = true,
}: {
  subdomain: SubdomainName;
  documentKind: DocumentKind;
  documentId: DocumentId;
  title?: string;
  defaultOpen?: boolean;
}) {
  const [request] = useState<ListRequest>({
    subdomain: { active: true, values: [subdomain] },
    documentKind: { active: true, values: [documentKind] },
    documentId: { active: true, values: [documentId] },
  });
  const [selected, setSelected] = useState<Textblock[]>([]);
  const { companyId } = useActiveCompany();
  const [open, setOpen] = useState(defaultOpen);
  const { data } = useTextblocksTextblockListQuery(request);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card>
        <CardHeader>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="group justify-start">
              <CardTitle className="-ml-2 text-base">{title}</CardTitle>
              <ChevronUp className="group-data-[state=open]:rotate-180" />
            </Button>
          </CollapsibleTrigger>
          {open && (
            <CardAction>
              <div className="flex gap-2">
                <CopyFromCatalogDialog
                  companyId={companyId}
                  subdomain={subdomain}
                  documentKind={documentKind}
                  documentId={documentId}
                  disabled={data?.items.some((tb) => tb.immutable) || false}
                />
                <DeleteTextblocksDialog selected={selected} />
                <CreateTextblockDialog
                  subdomain={subdomain}
                  documentKind={documentKind}
                  documentId={documentId}
                  disabled={data?.items.some((tb) => tb.immutable) || false}
                />
              </div>
            </CardAction>
          )}
        </CardHeader>
        <CollapsibleContent>
          <CardContent>
            <TextblocksTable
              request={request}
              selected={selected}
              setSelected={setSelected}
            />
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

function CopyFromCatalogDialog({
  companyId,
  subdomain,
  documentKind,
  documentId,
  disabled,
}: {
  companyId: UUID;
  subdomain: SubdomainName;
  documentKind: DocumentKind;
  documentId: DocumentId;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const { data, isLoading, error } = useTextblocksCatalogListQuery({
    company: { active: true, values: [companyId] },
  });
  const [
    put,
    {
      isLoading: putIsLoading,
      error: putError,
      isSuccess: putIsSuccess,
      reset,
    },
  ] = useTextblocksCopyFromCatalogMutation();

  useEffect(() => {
    if (putIsSuccess) {
      setOpen(false);
      reset();
    }
  }, [putIsSuccess, reset]);

  const doCopy = (catalogId: string) => {
    if (putIsLoading || disabled) return;

    put({
      sourceEntity: CpyName.TextblocksCatalog,
      sourceID: catalogId,
      destinationEntity: CpyName.TextblocksTextblockList,
      destinationID: `${subdomain}:${documentKind}:${documentId}`,
      meta: {
        doNotReplace: false,
        subdomain,
        documentKind,
        documentID: documentId,
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" disabled={disabled}>
          <Copy />
          <span>{t("Aus Katalog übernehmen")}</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{t("Aus Katalog übernehmen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Wählen Sie den Katalog aus welchem die Bemerkungnen übernommen werden sollen.",
            )}
          </DialogDescription>
        </DialogHeader>
        <RTKQueryErrorAlert error={putError} ignoreValidationError={false} />
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Name")}</TableHead>
              <TableHead>{t("Beschreibung")}</TableHead>
              <TableHead>{t("Aktualisiert am")}</TableHead>
              <TableHead>{t("Aktionen")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={3} />}
            {error && <TableRtkErrorRow colSpan={3} error={error} />}
            {data && data.items.length === 0 && <TableEmptyRow colSpan={3} />}
            {data &&
              data.items.map((catalog) => (
                <TableRow key={catalog.id}>
                  <TableCell>{catalog.name}</TableCell>
                  <TableCell className="trucate max-w-md">
                    {catalog.description}
                  </TableCell>
                  <TableCell>{formatDate(catalog.updatedAt)}</TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      onClick={() => doCopy(catalog.id)}
                      size="sm"
                    >
                      {t("Übernehmen")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
}
